import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { CoverPageBackground, CoverPageOrganizationName, CoverPageRow, InlineInput, useUpdatePage } from './Shared';
import {
  type SectionSettings,
  StudioPrintSettingsContext,
  useDebounceToGlobal,
  UserContext,
  type SectionPageT,
  useAppPrintMode,
} from 'venn-components';
import { validate as uuidValidate } from 'uuid';
import { useRecoilValue } from 'recoil';
import {
  DEFAULT_STOCK_PHOTO,
  getCustomFontSize,
  pageFonts,
  sectionPageBackgroundImage,
  studioSectionPageHasFullPageBackgroundImage,
  studioSectionPageHideCompanyName,
  viewPrimaryColor,
  viewSectionTextColor,
} from 'venn-state';
import { useHasFF } from 'venn-utils';

const SectionPage = ({ pageNumber, page }: { pageNumber: number; page: SectionPageT }) => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');

  const { inPrintMode } = useAppPrintMode();
  const { organizationPrintSettings } = useContext(StudioPrintSettingsContext);
  const { profileSettings } = useContext(UserContext);
  const updatePage = useUpdatePage(pageNumber);
  const primaryColor = useRecoilValue(viewPrimaryColor);
  const sectionTextColor = useRecoilValue(viewSectionTextColor);
  const hideCompanyName = useRecoilValue(studioSectionPageHideCompanyName);
  const hasFullPageBackgroundImage = useRecoilValue(studioSectionPageHasFullPageBackgroundImage);
  const backgroundImage = useRecoilValue(sectionPageBackgroundImage);
  const coverPageImageId = uuidValidate(backgroundImage) ? backgroundImage : DEFAULT_STOCK_PHOTO;
  const coverPageImage = `/api/images/${coverPageImageId}`;

  const shouldApplyFullPageBackground = hasCustomizationEnhancements && hasFullPageBackgroundImage;
  const shouldHideCompanyName = hasCustomizationEnhancements && hideCompanyName;
  const textColor = hasCustomizationEnhancements ? sectionTextColor : undefined;
  const backgroundColor = shouldApplyFullPageBackground ? 'transparent' : primaryColor;

  const onChangeSettings = useCallback(
    (settings: SectionSettings) => {
      updatePage({
        sectionSettings: settings,
      });
    },
    [updatePage],
  );

  const [sectionSettings, setSectionSettings] = useDebounceToGlobal(page.sectionSettings, onChangeSettings);

  const onTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const title = e.target.value;
      setSectionSettings((cur) => ({ ...cur, title }));
    },
    [setSectionSettings],
  );

  const onSubtitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const subTitle = e.target.value;
      setSectionSettings((cur) => ({ ...cur, subTitle }));
    },
    [setSectionSettings],
  );

  const onTopTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const topText = e.target.value;
      setSectionSettings((cur) => ({ ...cur, topText }));
    },
    [setSectionSettings],
  );

  const fontSizes = useFontSizes();

  return (
    <>
      <SectionPageWrapper color={backgroundColor}>
        <CoverPageRow height="33%">
          <SectionPageRow>
            <InlineInput
              style={{
                ...fontSizes.name,
                fontWeight: 700,
                color: textColor,
              }}
              $inPrintMode={inPrintMode}
              placeholder="<Enter Section Name>"
              extraWidth={inPrintMode && hasCustomizationEnhancements ? 50 : 0}
              value={sectionSettings.topText}
              onChange={onTopTextChange}
              data-testid="qa-section-page-name"
              placeholderColor={hasCustomizationEnhancements ? textColor : undefined}
            />
          </SectionPageRow>
        </CoverPageRow>
        <SectionTitleRow>
          <SectionTitleGroup>
            <InlineInput
              style={{
                ...fontSizes.title,
                fontWeight: 700,
                color: textColor,
              }}
              $inPrintMode={inPrintMode}
              placeholder="<Enter Title>"
              extraWidth={inPrintMode && hasCustomizationEnhancements ? 50 : 0}
              value={sectionSettings.title}
              onChange={onTitleChange}
              data-testid="qa-section-page-title"
              placeholderColor={hasCustomizationEnhancements ? textColor : undefined}
            />
            <InlineInput
              style={{
                ...fontSizes.subTitle,
                fontWeight: 300,
                color: textColor,
                display: 'block',
              }}
              $inPrintMode={inPrintMode}
              placeholder="<Enter Subtitle>"
              extraWidth={inPrintMode && hasCustomizationEnhancements ? 50 : 0}
              value={sectionSettings.subTitle}
              onChange={onSubtitleChange}
              data-testid="qa-section-page-subtitle"
              placeholderColor={hasCustomizationEnhancements ? textColor : undefined}
            />
          </SectionTitleGroup>
        </SectionTitleRow>
        <CoverPageRow height="33%">
          {!shouldHideCompanyName && (
            <BottomRow>
              <CoverPageOrganizationName
                style={{ ...fontSizes.orgName, color: textColor }}
                data-testid="qa-section-page-orgname"
              >
                {organizationPrintSettings?.organizationName ?? profileSettings?.organization.name}
              </CoverPageOrganizationName>
            </BottomRow>
          )}
        </CoverPageRow>
      </SectionPageWrapper>
      {shouldApplyFullPageBackground && <CoverPageBackground src={coverPageImage} />}
    </>
  );
};

export default SectionPage;

function useFontSizes() {
  const nameFont = useRecoilValue(pageFonts.sectionPageName);
  const titleFont = useRecoilValue(pageFonts.sectionPageTitle);
  const subTitleFont = useRecoilValue(pageFonts.sectionPageSubtitle);
  const orgNameFont = useRecoilValue(pageFonts.sectionPageOrgName);

  return {
    name: {
      fontSize: getCustomFontSize(nameFont),
      lineHeight: nameFont.lineHeight,
    },
    title: {
      fontSize: getCustomFontSize(titleFont),
      lineHeight: titleFont.lineHeight,
    },
    subTitle: {
      fontSize: getCustomFontSize(subTitleFont),
      lineHeight: subTitleFont.lineHeight,
    },
    orgName: {
      fontSize: getCustomFontSize(orgNameFont),
      lineHeight: orgNameFont.lineHeight,
    },
  };
}

export const BottomRow = styled.div`
  position: absolute;
  bottom: 40px;
  right: 40px;
  padding-bottom: 3px;
  vertical-align: text-bottom;
`;

const SectionPageWrapper = styled.div<{ color: string }>`
  background-color: ${({ color }) => color};
  color: white;
  height: 100%;
  display: grid;
`;

const SectionTitleRow = styled.div`
  position: relative;
  display: flex;
  top: 50%;
  transform: translateY(-50%);
  justify-content: flex-start;
  padding: 15px 30px;
  overflow: hidden;
  width: 100%;
`;

const SectionTitleGroup = styled.div`
  overflow: hidden;
  width: 100%;
`;

const SectionPageRow = styled.div`
  padding: 35px 30px;
`;
